import React, { useEffect, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import { Button } from "../../components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "../../components/ui/form";
import { cn } from "../../lib/utils";

interface ComboboxControllerProps {
  name: string;
  label: string;
  options: { value: string; label: string }[];
  defaultValue?: string | string[];
  infoMessage?: string;
  inline?: boolean;
  multiple?: boolean; // ✅ Enables multi-select
  onChange?: (value: string | string[]) => void;
}

const ComboboxController: React.FC<ComboboxControllerProps> = ({
  name,
  label,
  options,
  defaultValue = "",
  infoMessage,
  inline,
  multiple = false,
  onChange,
}) => {
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext();
  const [open, setOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<string>("");

  useEffect(() => {
    const currentValue = getValues(name) || defaultValue;
    if (multiple) {
      const values = Array.isArray(currentValue)
        ? currentValue
        : [currentValue];
      setSelectedValues(values);
    } else {
      setSelectedLabel(
        options.find((opt) => opt.value === currentValue)?.label || ""
      );
    }
    setValue(name, currentValue);
  }, [defaultValue, name, setValue, getValues, options, multiple]);

  const handleSelect = (selected: string) => {
    if (multiple) {
      let updatedSelection: string[];
      if (selectedValues.includes(selected)) {
        updatedSelection = selectedValues.filter((item) => item !== selected);
      } else {
        updatedSelection = [...selectedValues, selected];
      }
      setSelectedValues(updatedSelection);
      setValue(name, updatedSelection);
      if (onChange) onChange(updatedSelection);
    } else {
      setSelectedLabel(
        options.find((opt) => opt.value === selected)?.label || ""
      );
      setValue(name, selected);
      if (onChange) onChange(selected);
      setOpen(false);
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ fieldState: { error } }) => (
        <FormItem className={`mb-6 ${inline ? "flex items-center" : ""}`}>
          <FormLabel htmlFor={name} className="w-1/3">
            {label}
          </FormLabel>
          <FormControl className="w-2/3">
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between hover:bg-white"
                >
                  <span className="text-sm text-gray-800">
                    {multiple
                      ? selectedValues.length > 0
                        ? selectedValues
                            .map(
                              (val) =>
                                options.find((opt) => opt.value === val)?.label
                            )
                            .join(", ")
                        : `Select ${label}`
                      : selectedLabel || `Select ${label}`}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput
                    placeholder={`Search ${label.toLowerCase()}...`}
                  />
                  <CommandList>
                    <CommandEmpty>No results found.</CommandEmpty>
                    <CommandGroup>
                      {options.map((option) => (
                        <CommandItem
                          key={option.value}
                          value={option.value}
                          onSelect={() => handleSelect(option.value)}
                        >
                          {multiple && (
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedValues.includes(option.value)
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                          )}
                          {option.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </FormControl>
          {error && (
            <FormMessage className="text-red-500">{error.message}</FormMessage>
          )}
          {!error && infoMessage && (
            <FormMessage className="text-gray-500 text-xs font-normal">
              {infoMessage}
            </FormMessage>
          )}
        </FormItem>
      )}
    />
  );
};

export default ComboboxController;
